import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  Assessment,
  Warning as WarningIcon,
  CheckCircle,
  Info,
  History,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// ─── Constants ──────────────────────────────────────────────────────────────────

const REGIME = {
  RISK_ON:  { bg: 'linear-gradient(135deg,#059669,#10b981)', label: 'Tấn công', en: 'Risk On',  icon: <TrendingUp /> },
  RECOVERY: { bg: 'linear-gradient(135deg,#65a30d,#84cc16)', label: 'Hồi phục', en: 'Recovery', icon: <CheckCircle /> },
  SIDEWAY:  { bg: 'linear-gradient(135deg,#d97706,#f59e0b)', label: 'Đi ngang', en: 'Sideway',  icon: <Assessment /> },
  RISK_OFF: { bg: 'linear-gradient(135deg,#6366f1,#818cf8)', label: 'Phòng thủ', en: 'Risk Off', icon: <WarningIcon /> },
};
const ACTION = {
  BUY: { bg: '#059669', label: 'MUA VÀO', en: 'BUY' },
  HOLD: { bg: '#d97706', label: 'GIỮ', en: 'HOLD' },
  REDUCE: { bg: '#ea580c', label: 'GIẢM', en: 'REDUCE' },
  EXIT: { bg: '#dc2626', label: 'THOÁT', en: 'EXIT' },
};

// ─── Formatters ─────────────────────────────────────────────────────────────────

const f = (n, d = 0) => n == null || isNaN(n) ? '—' : Number(n).toLocaleString('vi-VN', { maximumFractionDigits: d, minimumFractionDigits: d });
const fp = (n, d = 1) => n == null || isNaN(n) ? '—' : `${Number(n).toFixed(d)}%`;
const fv = (n) => { if (n == null) return '—'; const v = Number(n); return v >= 1e9 ? `${(v / 1e9).toFixed(2)} tỷ` : v >= 1e6 ? `${(v / 1e6).toFixed(0)} tr` : f(v); };

// ─── Main ───────────────────────────────────────────────────────────────────────

const AutoPick = () => {
  const { t, i18n } = useTranslation();
  const vi = i18n.language === 'vi';

  const [tab, setTab] = useState(0);
  const [nav, setNav] = useState('1000000000');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // History state
  const [histLoading, setHistLoading] = useState(false);
  const [histData, setHistData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await axios.get(`${process.env.REACT_APP_API_URL}/portfolio/auto-pick`, { params: { nav: parseFloat(nav) } }); setData(r.data); }
    catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [nav]);

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try { const r = await axios.get(`${process.env.REACT_APP_API_URL}/portfolio/auto-pick/history`, { params: { limit: 30 } }); setHistData(r.data); }
    catch (e) { console.error(e); }
    finally { setHistLoading(false); }
  }, []);

  useEffect(() => { if (tab === 1 && !histData) fetchHistory(); }, [tab, histData, fetchHistory]);

  const pm = data?.portfolio_metrics || {};
  const rec = data?.recommendations || {};
  const pos = data?.positions || [];
  const rg = data?.regime;
  const rd = rg?.details || {};
  const rm = REGIME[rg?.state] || REGIME.SIDEWAY;
  const am = ACTION[rec.action] || ACTION.HOLD;
  const sectors = data?.sectors?.top_selected || [];
  const sAlloc = data?.sector_allocation || {};
  const sColors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6'];

  // Derived
  const avgScore = pos.length ? pos.reduce((s, p) => s + (p.stock_score || 0), 0) / pos.length : 0;
  const maxStop = pos.length ? Math.max(...pos.map(p => p.stop_pct || 0)) : 0;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* ─── Header ─── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
            {t('autoPick.title', 'Auto-Pick Portfolio')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
            {t('autoPick.subtitle', 'AI-powered portfolio construction with anti-chase protection')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TextField size="small" label="NAV" type="number" value={nav} onChange={e => setNav(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">VND</InputAdornment>, sx: { fontFamily: 'monospace', fontWeight: 600 } }}
            sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <Button variant="contained" onClick={fetch} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            sx={{ height: 40, borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none', fontSize: '0.85rem' }}>
            {loading ? 'Đang tạo...' : 'Tạo danh mục'}
          </Button>
        </Box>
      </Box>

      {/* ─── Tabs ─── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '0.85rem' } }}>
          <Tab label={vi ? 'Danh mục hôm nay' : 'Today Portfolio'} icon={<TrendingUp sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label={vi ? 'Lịch sử & hiệu suất' : 'History & Performance'} icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* ─── TAB 0: Skeleton Loading ─── */}
      {tab === 0 && loading && (
        <>
          {/* ══════ Hero Banner Skeleton ══════ */}
          <Box sx={{ bgcolor: '#e5e7eb', borderRadius: 2.5, mb: 2.5, overflow: 'hidden' }}>
            {/* Top row: regime + scores + action + sectors */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 3, pt: 2.5, pb: 1.5, flexWrap: 'wrap' }}>
              {/* Regime icon + label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'rgba(0,0,0,0.08)' }} />
                <Box>
                  <Skeleton width={90} height={10} sx={{ mb: 0.5, bgcolor: 'rgba(0,0,0,0.08)' }} />
                  <Skeleton width={110} height={24} sx={{ bgcolor: 'rgba(0,0,0,0.1)' }} />
                </Box>
              </Box>
              {/* Sub-score bars */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {['Trend', 'Breadth', 'Liquidity', 'Risk'].map(k => (
                  <Box key={k} sx={{ minWidth: 70 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                      <Skeleton width={40} height={9} sx={{ bgcolor: 'rgba(0,0,0,0.07)' }} />
                      <Skeleton width={18} height={9} sx={{ bgcolor: 'rgba(0,0,0,0.07)' }} />
                    </Box>
                    <Skeleton variant="rounded" height={3} sx={{ borderRadius: 1, bgcolor: 'rgba(0,0,0,0.1)' }} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ flex: 1 }} />
              {/* Action badge */}
              <Skeleton variant="rounded" width={60} height={26} sx={{ borderRadius: 1, bgcolor: 'rgba(0,0,0,0.1)' }} />
              {/* Sector chips */}
              <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: 3, bgcolor: 'rgba(0,0,0,0.08)' }} />
              <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 3, bgcolor: 'rgba(0,0,0,0.08)' }} />
            </Box>

            {/* Bottom row: 6 KPI cells */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', bgcolor: 'rgba(0,0,0,0.06)', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              {[0, 1, 2, 3, 4, 5].map(i => (
                <Box key={i} sx={{ px: 2, py: 1.5, textAlign: 'center', borderRight: i < 5 ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
                  <Skeleton width={50} height={9} sx={{ mx: 'auto', mb: 0.5, bgcolor: 'rgba(0,0,0,0.07)' }} />
                  <Skeleton width={65} height={20} sx={{ mx: 'auto', mb: 0.3, bgcolor: 'rgba(0,0,0,0.1)' }} />
                  <Skeleton width={55} height={9} sx={{ mx: 'auto', bgcolor: 'rgba(0,0,0,0.06)' }} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* ══════ Table Skeleton ══════ */}
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', mb: 2.5, overflow: 'hidden' }}>
            {/* Title bar */}
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'grey.100', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton width={150} height={20} />
              <Skeleton width={120} height={14} />
            </Box>
            {/* Table header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '36px 60px 1fr 50px 70px 60px 70px 60px 60px 55px 50px', gap: 1, px: 2.5, py: 1.2, borderBottom: '2px solid', borderColor: 'grey.200' }}>
              {['#', 'Mã', 'Ngành', 'Điểm', 'Tỷ trọng', 'Số CP', 'Giá trị', 'Giá vào', 'Stop', 'Stop%', 'Risk'].map(h => (
                <Skeleton key={h} height={12} sx={{ bgcolor: 'grey.200' }} />
              ))}
            </Box>
            {/* Rows */}
            {[0, 1, 2, 3, 4].map(i => (
              <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '36px 60px 1fr 50px 70px 60px 70px 60px 60px 55px 50px', gap: 1, px: 2.5, py: 1.4, borderBottom: '1px solid', borderColor: 'grey.50', alignItems: 'center' }}>
                <Skeleton width={14} height={14} />
                <Skeleton width={36} height={16} sx={{ bgcolor: '#dbeafe' }} />
                <Skeleton width="70%" height={12} />
                <Skeleton variant="rounded" width={34} height={20} sx={{ borderRadius: 0.8 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="rounded" width={30} height={4} sx={{ borderRadius: 1 }} />
                  <Skeleton width={32} height={14} />
                </Box>
                <Skeleton width={40} height={14} />
                <Skeleton width={50} height={14} />
                <Skeleton width={42} height={14} />
                <Skeleton width={42} height={14} sx={{ bgcolor: '#fee2e2' }} />
                <Skeleton width={36} height={14} sx={{ bgcolor: '#fee2e2' }} />
                <Skeleton width={32} height={14} />
              </Box>
            ))}
            {/* Footer */}
            <Box sx={{ px: 2.5, py: 1.2, borderTop: '2px solid', borderColor: 'grey.200', bgcolor: 'grey.50', display: 'flex', gap: 4 }}>
              {[{ w: 55 }, { w: 45 }, { w: 45 }, { w: 65 }].map((x, i) => (
                <Box key={i}>
                  <Skeleton width={35} height={9} sx={{ mb: 0.4 }} />
                  <Skeleton width={x.w} height={15} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* ══════ Bottom Cards Skeleton ══════ */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Sector allocation */}
            <Box sx={{ flex: '1 1 260px', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 2 }}>
              <Skeleton width={110} height={18} sx={{ mb: 1.5 }} />
              <Skeleton variant="rounded" height={10} sx={{ borderRadius: 1, mb: 1.5 }} />
              {[0, 1].map(j => (
                <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                  <Skeleton variant="circular" width={8} height={8} />
                  <Skeleton width="50%" height={12} />
                  <Skeleton variant="rounded" width={50} height={4} sx={{ borderRadius: 1, ml: 'auto' }} />
                  <Skeleton width={30} height={12} />
                </Box>
              ))}
            </Box>
            {/* Recommendations */}
            <Box sx={{ flex: '1 1 260px', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 2 }}>
              <Skeleton width={100} height={18} sx={{ mb: 1.5 }} />
              {[0, 1, 2].map(j => (
                <Box key={j} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Skeleton variant="circular" width={16} height={16} sx={{ flexShrink: 0, mt: 0.2 }} />
                  <Skeleton width="85%" height={14} />
                </Box>
              ))}
            </Box>
            {/* Warnings */}
            <Box sx={{ flex: '1 1 260px', bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid', borderColor: '#fde68a', p: 2 }}>
              <Skeleton width={80} height={18} sx={{ mb: 1.5 }} />
              {[0, 1].map(j => (
                <Box key={j} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Skeleton variant="circular" width={16} height={16} sx={{ flexShrink: 0, mt: 0.2, bgcolor: '#fde68a' }} />
                  <Skeleton width="80%" height={14} sx={{ bgcolor: '#fef3c7' }} />
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* ─── TAB 0: Today Portfolio ─── */}
      {tab === 0 && !loading && data?.success && (
        <>
          {/* ══════════ HERO BANNER ══════════ */}
          <Box sx={{ background: rm.bg, borderRadius: 2.5, color: '#fff', mb: 2.5, overflow: 'hidden', position: 'relative' }}>
            {/* Decorative */}
            <Box sx={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
            <Box sx={{ position: 'absolute', bottom: -30, right: 120, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

            {/* Top row: Regime + Sub-scores + Action + Sectors */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 3, pt: 2.5, pb: 1.5, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
              {/* Regime */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                <Box sx={{ '& svg': { fontSize: 32 } }}>{rm.icon}</Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', opacity: 0.7, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>Market Regime</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{vi ? rm.label : rm.en}</Typography>
                </Box>
              </Box>

              {/* Sub-scores: compact inline */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                  { k: 'Trend', v: rd.trend_score },
                  { k: 'Breadth', v: rd.breadth_score },
                  { k: 'Liquidity', v: rd.liquidity_score },
                  { k: 'Risk', v: rd.risk_score },
                ].map(s => (
                  <Box key={s.k} sx={{ minWidth: 70 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.58rem', opacity: 0.7 }}>{s.k}</Typography>
                      <Typography sx={{ fontSize: '0.58rem', fontFamily: 'monospace', fontWeight: 700 }}>{s.v?.toFixed(0)}</Typography>
                    </Box>
                    <Box sx={{ height: 3, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1 }}>
                      <Box sx={{ height: '100%', width: `${Math.min(s.v || 0, 100)}%`, bgcolor: 'rgba(255,255,255,0.75)', borderRadius: 1 }} />
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Action badge */}
              <Box sx={{ bgcolor: am.bg, px: 1.5, py: 0.4, borderRadius: 1, border: '1px solid rgba(255,255,255,0.25)' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', letterSpacing: 0.5 }}>{vi ? am.label : am.en}</Typography>
              </Box>

              {/* Sectors */}
              {sectors.map((s, i) => (
                <Chip key={i} label={s.sector_name?.[vi ? 'name_vi' : 'name'] || s.sector_code} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: '0.68rem', height: 24, fontWeight: 600, '& .MuiChip-label': { px: 1 } }} />
              ))}
            </Box>

            {/* Bottom row: KPI strip */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)',
              bgcolor: 'rgba(0,0,0,0.12)', borderTop: '1px solid rgba(255,255,255,0.12)',
              position: 'relative', zIndex: 1,
            }}>
              {[
                { label: vi ? 'Vị thế' : 'Positions', big: `${pm.num_positions || 0}`, sub: `avg score ${avgScore.toFixed(1)}` },
                { label: vi ? 'Giải ngân' : 'Exposure', big: fp(pm.exposure_pct), sub: `target ${fp(rec.target_exposure_pct)}` },
                { label: vi ? 'Tiền mặt' : 'Cash', big: fv(data.nav * (pm.cash_pct || 0) / 100), sub: fp(pm.cash_pct, 0) },
                { label: vi ? 'Tổng đầu tư' : 'Invested', big: fv(pm.total_value), sub: `${Object.keys(sAlloc).length} ${vi ? 'ngành' : 'sectors'}` },
                { label: vi ? 'Rủi ro' : 'Risk', big: fp(pm.total_risk_pct), sub: fv(pm.total_risk_amount) },
                { label: vi ? 'Max stop' : 'Max Stop', big: maxStop > 0 ? `-${fp(maxStop)}` : '—', sub: `avg ${fp(pos.length ? pos.reduce((s, p) => s + (p.stop_pct || 0), 0) / pos.length : 0)}` },
              ].map((kpi, i) => (
                <Box key={i} sx={{
                  px: 2, py: 1.5, textAlign: 'center',
                  borderRight: i < 5 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}>
                  <Typography sx={{ fontSize: '0.58rem', opacity: 0.65, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {kpi.label}
                  </Typography>
                  <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1.3 }}>
                    {kpi.big}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', opacity: 0.6 }}>{kpi.sub}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ══════════ POSITIONS TABLE ══════════ */}
          {pos.length > 0 ? (
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', mb: 2.5, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                  {t('autoPick.recommendedPositions', 'Danh mục đề xuất')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.snapshot_date} · {pm.num_positions} {vi ? 'cổ phiếu' : 'stocks'}
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', color: 'grey.600', textTransform: 'uppercase', letterSpacing: 0.3, py: 1.2, borderBottom: '2px solid', borderColor: 'grey.200' } }}>
                      <TableCell sx={{ width: 36 }}>#</TableCell>
                      <TableCell>{vi ? 'Mã' : 'Ticker'}</TableCell>
                      <TableCell>{vi ? 'Ngành' : 'Sector'}</TableCell>
                      <TableCell align="right">{vi ? 'Điểm' : 'Score'}</TableCell>
                      <TableCell align="right">{vi ? 'Tỷ trọng' : 'Weight'}</TableCell>
                      <TableCell align="right">{vi ? 'Số CP' : 'Shares'}</TableCell>
                      <TableCell align="right">{vi ? 'Giá trị' : 'Value'}</TableCell>
                      <TableCell align="right">{vi ? 'Giá vào' : 'Entry'}</TableCell>
                      <TableCell align="right">Stop</TableCell>
                      <TableCell align="right">Stop %</TableCell>
                      <TableCell align="right">Risk</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pos.map((p, i) => {
                      const sn = typeof p.sector_name === 'string' ? p.sector_name : (vi ? p.sector_name?.name_vi : p.sector_name?.name) || p.sector_code;
                      return (
                        <TableRow key={i} hover sx={{ '&:last-child td': { borderBottom: 0 }, '& td': { py: 1.3, fontSize: '0.82rem' } }}>
                          <TableCell><Typography variant="caption" sx={{ color: 'grey.400', fontWeight: 600 }}>{i + 1}</Typography></TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{p.ticker}</Typography></TableCell>
                          <TableCell><Typography variant="caption" sx={{ color: 'grey.600' }}>{sn}</Typography></TableCell>
                          <TableCell align="right">
                            <Box sx={{
                              display: 'inline-flex', px: 0.8, py: 0.15, borderRadius: 0.8, fontWeight: 700, fontFamily: 'monospace', fontSize: '0.76rem',
                              bgcolor: p.stock_score >= 35 ? '#dcfce7' : p.stock_score >= 20 ? '#fef9c3' : '#fef2f2',
                              color: p.stock_score >= 35 ? '#166534' : p.stock_score >= 20 ? '#854d0e' : '#991b1b',
                            }}>{p.stock_score?.toFixed(1)}</Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <Box sx={{ width: 36, height: 4, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                                <Box sx={{ width: `${Math.min(p.size_pct_nav * 100 / 15, 100)}%`, height: '100%', bgcolor: '#3b82f6', borderRadius: 1 }} />
                              </Box>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, minWidth: 40 }}>{fp(p.size_pct_nav)}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{f(p.size_shares)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{fv(p.size_value)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{f(p.entry_price)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#dc2626' }}>{f(p.stop_price)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#dc2626', fontWeight: 600 }}>-{fp(p.stop_pct)}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: p.risk_pct > 0.5 ? '#dc2626' : '#16a34a' }}>{fp(p.risk_pct)}</Typography></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Footer */}
              <Box sx={{ px: 2.5, py: 1.2, borderTop: '2px solid', borderColor: 'grey.200', bgcolor: 'grey.50', display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                {[
                  { l: vi ? 'Tổng' : 'Total', v: fv(pm.total_value) },
                  { l: vi ? 'TB/vị thế' : 'Avg', v: fp(pm.avg_position_size_pct) },
                  { l: vi ? 'Max/vị thế' : 'Max', v: fp(pm.max_position_size_pct) },
                  { l: vi ? 'Tổng rủi ro' : 'Total risk', v: fv(pm.total_risk_amount), c: '#dc2626' },
                ].map(x => (
                  <Box key={x.l}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'grey.500', fontWeight: 600 }}>{x.l}</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 700, color: x.c || 'text.primary' }}>{x.v}</Typography>
                  </Box>
                ))}
                <Box sx={{ ml: 'auto' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: 'grey.300' }}>{f(data.calculation_time_ms, 0)}ms</Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {vi ? 'Không có vị thế đề xuất' : 'No positions recommended'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {vi ? 'Chưa có setup đạt điều kiện hoặc thị trường chưa thuận lợi.' : 'No valid entry setups or unfavorable conditions.'}
              </Typography>
            </Alert>
          )}

          {/* ══════════ BOTTOM: Sector + Recs + Warnings ══════════ */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Sector allocation */}
            {Object.keys(sAlloc).length > 0 && (
              <Box sx={{ flex: '1 1 260px', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.82rem' }}>
                  {vi ? 'Phân bổ ngành' : 'Sector Allocation'}
                </Typography>
                {/* Stacked bar */}
                <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
                  {Object.entries(sAlloc).sort(([, a], [, b]) => b - a).map(([, w], i) => (
                    <Box key={i} sx={{ width: `${w}%`, bgcolor: sColors[i % sColors.length], minWidth: 6 }} />
                  ))}
                  <Box sx={{ flex: 1, bgcolor: '#f1f5f9' }} />
                </Box>
                {Object.entries(sAlloc).sort(([, a], [, b]) => b - a).map(([name, w], i) => (
                  <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sColors[i % sColors.length], flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', flex: 1 }}>{name.replace(/_/g, ' ')}</Typography>
                    <Box sx={{ width: 60, height: 4, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${Math.min(w * 100 / 40, 100)}%`, bgcolor: sColors[i % sColors.length], borderRadius: 1 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 700, minWidth: 38, textAlign: 'right' }}>{fp(w)}</Typography>
                  </Box>
                ))}
                <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.62rem', mt: 0.5, display: 'block' }}>
                  Max sector: {fp(data.max_sector_weight)} / 40% cap
                </Typography>
              </Box>
            )}

            {/* Recommendations */}
            {rec.items?.length > 0 && (
              <Box sx={{ flex: '1 1 260px', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.82rem' }}>
                  {vi ? 'Khuyến nghị' : 'Recommendations'}
                </Typography>
                {rec.items.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <Info sx={{ fontSize: 16, color: '#3b82f6', mt: 0.2 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Warnings */}
            {rec.warnings?.length > 0 && (
              <Box sx={{ flex: '1 1 260px', bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid', borderColor: '#fde68a', p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.82rem', color: '#92400e' }}>
                  {vi ? 'Cảnh báo' : 'Warnings'}
                </Typography>
                {rec.warnings.map((w, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <WarningIcon sx={{ fontSize: 16, color: '#d97706', mt: 0.2 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.5, color: '#78350f' }}>{w}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* ─── TAB 1: History & Performance ─── */}
      {tab === 1 && (
        <Box>
          {/* Stats cards skeleton */}
          {histLoading && !histData && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2.5 }}>
              {[0, 1, 2, 3, 4, 5].map(i => (
                <Box key={i} sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 2, textAlign: 'center' }}>
                  <Skeleton width={80} height={12} sx={{ mx: 'auto', mb: 0.8 }} />
                  <Skeleton width={60} height={32} sx={{ mx: 'auto' }} />
                </Box>
              ))}
            </Box>
          )}

          {/* Stats cards */}
          {!histLoading && histData?.stats && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2.5 }}>
              {[
                { label: vi ? 'Tổng snapshot' : 'Total Snapshots', value: histData.stats.total_snapshots, color: '#3b82f6' },
                { label: vi ? 'TB lợi nhuận 5d' : 'Avg 5d Return', value: histData.stats.avg_5d_return != null ? `${histData.stats.avg_5d_return > 0 ? '+' : ''}${histData.stats.avg_5d_return}%` : '—', color: histData.stats.avg_5d_return > 0 ? '#16a34a' : '#dc2626' },
                { label: vi ? 'Tổng thắng' : 'Total Wins', value: histData.stats.total_wins, color: '#16a34a' },
                { label: vi ? 'Tổng thua' : 'Total Losses', value: histData.stats.total_losses, color: '#dc2626' },
                { label: 'Win Rate', value: histData.stats.win_rate != null ? `${histData.stats.win_rate}%` : '—', color: '#8b5cf6' },
                { label: vi ? '% ngày dương' : 'Positive Days', value: histData.stats.positive_day_rate != null ? `${histData.stats.positive_day_rate}%` : '—', color: '#0891b2' },
              ].map((s, i) => (
                <Box key={i} sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 2, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.65rem', color: 'grey.500', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, fontFamily: 'monospace', color: s.color }}>{s.value}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Refresh button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button size="small" startIcon={histLoading ? <CircularProgress size={14} /> : <Refresh />}
              onClick={fetchHistory} disabled={histLoading}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
              {vi ? 'Cập nhật' : 'Refresh'}
            </Button>
          </Box>

          {/* History skeleton */}
          {histLoading && (
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.3, borderBottom: '2px solid', borderColor: 'grey.200' }}>
                {[24, 80, 70, 60, 50, 55, 45, 50, 50, 50, 40].map((w, i) => (
                  <Skeleton key={i} width={w} height={14} />
                ))}
              </Box>
              {/* Rows */}
              {[0, 1, 2, 3, 4, 5].map(i => (
                <Box key={i} sx={{ display: 'flex', gap: 2, px: 2, py: 1.4, borderBottom: '1px solid', borderColor: 'grey.100', alignItems: 'center' }}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton width={80} height={16} />
                  <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rounded" width={55} height={20} sx={{ borderRadius: 0.8 }} />
                  <Skeleton width={30} height={16} />
                  <Skeleton width={45} height={16} />
                  <Skeleton width={45} height={16} />
                  <Skeleton width={50} height={16} />
                  <Skeleton width={50} height={16} />
                  <Skeleton width={50} height={16} />
                  <Skeleton width={40} height={16} />
                </Box>
              ))}
            </Box>
          )}

          {/* History table */}
          {!histLoading && histData?.history?.length > 0 ? (
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.7rem', color: 'grey.600', textTransform: 'uppercase', letterSpacing: 0.3, py: 1.2, borderBottom: '2px solid', borderColor: 'grey.200' } }}>
                      <TableCell sx={{ width: 30 }}></TableCell>
                      <TableCell>{vi ? 'Ngày' : 'Date'}</TableCell>
                      <TableCell>Regime</TableCell>
                      <TableCell align="center">{vi ? 'Hành động' : 'Action'}</TableCell>
                      <TableCell align="right">{vi ? 'Vị thế' : 'Positions'}</TableCell>
                      <TableCell align="right">{vi ? 'Giải ngân' : 'Exposure'}</TableCell>
                      <TableCell align="right">{vi ? 'Rủi ro' : 'Risk'}</TableCell>
                      <TableCell align="right">Ret 1d</TableCell>
                      <TableCell align="right">Ret 5d</TableCell>
                      <TableCell align="right">Ret 10d</TableCell>
                      <TableCell align="center">W/L</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {histData.history.map((snap, idx) => {
                      const rm2 = REGIME[snap.regime] || REGIME.SIDEWAY;
                      const am2 = ACTION[snap.action] || ACTION.HOLD;
                      const expanded = expandedRow === idx;
                      const retColor = (v) => v == null ? 'grey.400' : v > 0 ? '#16a34a' : v < 0 ? '#dc2626' : 'grey.600';
                      const retFmt = (v) => v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
                      
                      return (
                        <React.Fragment key={idx}>
                          <TableRow
                            hover
                            onClick={() => setExpandedRow(expanded ? null : idx)}
                            sx={{ cursor: 'pointer', '& td': { py: 1.2, fontSize: '0.8rem' }, bgcolor: expanded ? 'grey.50' : 'transparent' }}
                          >
                            <TableCell>
                              <IconButton size="small" sx={{ p: 0.2 }}>
                                {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell><Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.78rem' }}>{snap.date}</Typography></TableCell>
                            <TableCell>
                              <Chip label={vi ? rm2.label : rm2.en} size="small"
                                sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                                  background: rm2.bg, '& .MuiChip-label': { px: 1 } }} />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'inline-block', bgcolor: am2.bg, color: '#fff', px: 1, py: 0.15, borderRadius: 0.8, fontSize: '0.65rem', fontWeight: 700 }}>
                                {vi ? am2.label : am2.en}
                              </Box>
                            </TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{snap.num_positions}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{fp(snap.exposure_pct)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#dc2626' }}>{fp(snap.total_risk_pct)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: retColor(snap.portfolio_return_1d) }}>{retFmt(snap.portfolio_return_1d)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: retColor(snap.portfolio_return_5d) }}>{retFmt(snap.portfolio_return_5d)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: retColor(snap.portfolio_return_10d) }}>{retFmt(snap.portfolio_return_10d)}</Typography></TableCell>
                            <TableCell align="center">
                              {snap.win_count != null ? (
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  <span style={{ color: '#16a34a', fontWeight: 700 }}>{snap.win_count}</span>
                                  <span style={{ color: '#9ca3af' }}>/</span>
                                  <span style={{ color: '#dc2626', fontWeight: 700 }}>{snap.loss_count}</span>
                                </Typography>
                              ) : <Typography variant="body2" sx={{ color: 'grey.400' }}>—</Typography>}
                            </TableCell>
                          </TableRow>

                          {/* Expanded: positions detail */}
                          <TableRow>
                            <TableCell colSpan={11} sx={{ py: 0, px: 0, borderBottom: expanded ? '2px solid #e5e7eb' : 'none' }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 4, py: 2, bgcolor: '#f8fafc' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'grey.600', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                                    {vi ? 'Chi tiết vị thế' : 'Position Details'} — {snap.date}
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.65rem', color: 'grey.500', textTransform: 'uppercase', py: 0.8 } }}>
                                        <TableCell>{vi ? 'Mã' : 'Ticker'}</TableCell>
                                        <TableCell>{vi ? 'Ngành' : 'Sector'}</TableCell>
                                        <TableCell align="right">{vi ? 'Điểm' : 'Score'}</TableCell>
                                        <TableCell align="right">{vi ? 'Giá vào' : 'Entry'}</TableCell>
                                        <TableCell align="right">Stop</TableCell>
                                        <TableCell align="right">Stop %</TableCell>
                                        <TableCell align="center">{vi ? 'Lý do' : 'Reason'}</TableCell>
                                        <TableCell align="right">{vi ? 'Tỷ trọng' : 'Weight'}</TableCell>
                                        <TableCell align="right">1d</TableCell>
                                        <TableCell align="right">5d</TableCell>
                                        <TableCell align="right">10d</TableCell>
                                        <TableCell align="right">{vi ? 'Max tăng' : 'Max Gain'}</TableCell>
                                        <TableCell align="right">{vi ? 'Max giảm' : 'Max DD'}</TableCell>
                                        <TableCell align="center">{vi ? 'Kết quả' : 'Outcome'}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(snap.positions || []).map((p, pi) => {
                                        const outcomeColor = { WIN: '#16a34a', LOSS: '#dc2626', STOPPED: '#dc2626', ACTIVE: '#3b82f6' };
                                        const outcomeLabel = { WIN: vi ? 'Thắng' : 'Win', LOSS: vi ? 'Thua' : 'Loss', STOPPED: 'Stopped', ACTIVE: vi ? 'Đang theo dõi' : 'Active' };
                                        return (
                                          <TableRow key={pi} sx={{ '& td': { fontSize: '0.78rem', py: 0.8 } }}>
                                            <TableCell><Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{p.ticker}</Typography></TableCell>
                                            <TableCell><Typography variant="caption" sx={{ color: 'grey.500' }}>{p.sector?.replace(/_/g, ' ')}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.score?.toFixed(1) || '—'}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{f(p.entry)}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#dc2626' }}>{f(p.stop)}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#dc2626' }}>-{fp(p.stop_pct)}</Typography></TableCell>
                                            <TableCell align="center">
                                              <Chip label={p.stop_reason || '—'} size="small"
                                                sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }} />
                                            </TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{fp(p.size_pct)}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: retColor(p.return_1d) }}>{retFmt(p.return_1d)}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: retColor(p.return_5d) }}>{retFmt(p.return_5d)}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: retColor(p.return_10d) }}>{retFmt(p.return_10d)}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#16a34a', fontWeight: 600 }}>{p.max_gain != null ? `+${p.max_gain.toFixed(1)}%` : '—'}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#dc2626', fontWeight: 600 }}>{p.max_dd != null ? `${p.max_dd.toFixed(1)}%` : '—'}</Typography></TableCell>
                                            <TableCell align="center">
                                              {p.outcome ? (
                                                <Chip label={outcomeLabel[p.outcome] || p.outcome} size="small"
                                                  sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, color: '#fff',
                                                    bgcolor: outcomeColor[p.outcome] || '#9ca3af' }} />
                                              ) : <Typography variant="body2" sx={{ color: 'grey.400', fontSize: '0.7rem' }}>{vi ? 'Chờ dữ liệu' : 'Pending'}</Typography>}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : !histLoading && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {vi ? 'Chưa có dữ liệu lịch sử' : 'No history data yet'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {vi
                  ? 'Hệ thống sẽ tự động lưu snapshot mỗi ngày lúc 19:00. Sau vài ngày bạn sẽ thấy dữ liệu hiệu suất (1d, 5d, 10d return).'
                  : 'The system will automatically save daily snapshots at 7 PM. Performance data (1d, 5d, 10d returns) will appear after a few days.'}
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {/* ─── Empty state ─── */}
      {tab === 0 && !data && !loading && (
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 4, textAlign: 'center', maxWidth: 560, mx: 'auto', mt: 4 }}>
          <Box sx={{ mb: 2, color: 'grey.300', '& svg': { fontSize: 48 } }}><TrendingUp /></Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            {vi ? 'Cách hoạt động' : 'How It Works'}
          </Typography>
          <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
            {[
              vi ? 'Phát hiện regime thị trường (Risk On / Off / Recovery / Sideway)' : 'Detect market regime',
              vi ? 'Chọn ngành dẫn dắt dựa trên sector rotation' : 'Select leading sectors',
              vi ? 'Lọc cổ phiếu có setup breakout tốt' : 'Filter stocks with strong setups',
              vi ? 'Anti-chase filter: loại mã quá nóng' : 'Anti-chase filter: reject overheated stocks',
              vi ? 'Sizing vị thế theo ATR & risk budget' : 'Size positions by ATR & risk budget',
            ].map((step, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.2, alignItems: 'flex-start' }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, flexShrink: 0 }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ color: 'grey.700', lineHeight: 1.5 }}>{step}</Typography>
              </Box>
            ))}
          </Box>
          <Button variant="contained" onClick={fetch} startIcon={<Refresh />}
            sx={{ mt: 2.5, borderRadius: 2, px: 4, fontWeight: 700, textTransform: 'none' }}>
            {vi ? 'Tạo danh mục' : 'Generate Portfolio'}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default AutoPick;
